import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

// KaziPeople design-system Button: radius 10, weight 600, warm-orange primary.
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      // ⚠️ Tokens, and no dark: twins. Each variant carried a light literal plus
      // a dark: override built from Tailwind's own palette (red-300, white/10,
      // white/75) rather than the KaziPeople ramp, so the two halves disagreed.
      // A token resolves per theme on its own.
      //
      // `default` and `link` were already correct: this config overrides
      // Tailwind's orange scale, so orange-500 IS the brand #F28A3A and
      // orange-600 is --color-primary-dark.
      //
      // Hover uses brightness rather than an opacity modifier: the soft tokens
      // have no -ch channel variables, so bg-danger-soft/70 would emit nothing.
      variant: {
        default: "bg-orange-500 text-white hover:bg-orange-600",
        destructive:
          "bg-danger-soft text-danger hover:brightness-95",
        outline:
          "border-[1.5px] border-border bg-transparent text-foreground hover:bg-cream-dark",
        secondary:
          "bg-cream-dark text-foreground hover:bg-cream-deep",
        ghost: "text-charcoal-mid hover:bg-cream-dark",
        link: "text-orange-500 underline-offset-4 hover:underline",
      },
      size: {
        default: "px-5 py-[9px] text-[13px]",
        sm: "px-[14px] py-1.5 text-xs gap-1",
        lg: "px-7 py-3 text-[15px] gap-2",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
